<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  variant: {
    type: String,
    default: 'default',
  },
});

const isOffline = computed(
  () => props.variant === 'agent' && props.option.status === 'offline'
);
const isMobile = computed(
  () =>
    props.variant === 'agent' && props.option.lastActivitySource === 'mobile'
);
const lastActivityAt = computed(() => Number(props.option.lastActivityAt || 0));
const isRecentlyActive = computed(
  () =>
    isOffline.value &&
    lastActivityAt.value > 0 &&
    Date.now() / 1000 - lastActivityAt.value <= 60 * 60
);
const lastActivityLabel = computed(() => {
  if (!isOffline.value || !lastActivityAt.value) return '';

  const elapsedMinutes = Math.max(
    1,
    Math.floor((Date.now() / 1000 - lastActivityAt.value) / 60)
  );
  if (elapsedMinutes < 60) return `${elapsedMinutes} min`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours} h`;

  return `${Math.floor(elapsedHours / 24)} d`;
});
</script>

<template>
  <div class="menu group text-n-slate-12 min-h-7 min-w-0" role="button">
    <fluent-icon
      v-if="variant === 'icon' && option.icon"
      :icon="option.icon"
      size="14"
      class="flex-shrink-0"
    />
    <span
      v-if="
        (variant === 'label' || variant === 'label-assigned') && option.color
      "
      class="label-pill flex-shrink-0"
      :style="{ backgroundColor: option.color }"
    />
    <Avatar
      v-if="variant === 'agent'"
      :name="option.label"
      :src="option.thumbnail"
      :icon-name="option.iconName"
      :status="option.status === 'online' ? option.status : null"
      :size="20"
      class="flex-shrink-0"
    >
      <template v-if="option.iconName && option.thumbnail" #badge>
        <div
          class="absolute z-20 flex items-center justify-center rounded-full outline outline-1 outline-n-weak bg-n-solid-1 -bottom-0.5 ltr:-right-0.5 rtl:-left-0.5 size-3"
        >
          <Icon icon="i-lucide-bot" class="text-n-slate-11 size-2" />
        </div>
      </template>
    </Avatar>
    <div class="min-w-0 flex-1 mx-2">
      <div class="flex items-center min-w-0 gap-1">
        <p class="truncate min-w-0 m-0 text-xs">
          {{ option.label }}
        </p>
        <Icon
          v-if="isMobile"
          icon="i-lucide-smartphone"
          class="flex-shrink-0 text-n-blue-11 group-hover:text-white size-3"
        />
      </div>
      <div
        v-if="lastActivityLabel"
        class="flex items-center gap-1 mt-0.5 text-[10px] leading-3 text-n-slate-11 group-hover:text-white"
      >
        <span
          v-if="isRecentlyActive"
          class="flex-shrink-0 rounded-full bg-n-blue-9 group-hover:bg-white size-1.5"
        />
        <span>{{ lastActivityLabel }}</span>
      </div>
    </div>
    <Icon
      v-if="variant === 'label-assigned'"
      icon="i-lucide-check"
      class="flex-shrink-0 size-3.5 text-n-brand group-hover:text-white"
    />
  </div>
</template>

<style scoped lang="scss">
.menu {
  width: calc(6.25rem * 2);
  @apply flex items-center flex-nowrap p-1 rounded-md overflow-hidden cursor-pointer;

  &:hover {
    @apply bg-n-brand text-white;
  }
}

.agent-thumbnail {
  margin-top: 0 !important;
}

.label-pill {
  @apply w-4 h-4 rounded-full border border-n-strong border-solid flex-shrink-0;
}
</style>
